import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  AlertTriangle,
  Ban,
  Check,
  CircleStop,
  MoreHorizontal,
  Paperclip,
  Pause,
  Play,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { PageLoader } from "@/components/ui/Spinner";
import { EmptyState, TableShell, Th } from "@/features/power_projects/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLeads } from "@/features/leads/api/leads";
import { useLeadSearches } from "@/features/leads/api/searches";
import {
  CAMPAIGN_STATUS_COLORS,
  MESSAGE_STATUS_COLORS,
  useAddRecipients,
  useApproveMessages,
  useCampaign,
  useCampaignAction,
  useCampaignMessage,
  useCampaignMessages,
  useCancelMessages,
  useFinishCampaign,
  usePersonalizeCampaign,
  useQueueState,
  useSpamChecks,
  useRunSpamCheck,
  useRecomposeMessage,
  useResumeRecipient,
  useSaveMessage,
  type AddRecipientsOutcome,
  type CampaignBodyFormat,
  type CampaignMessage,
  type FinishOutcome,
} from "@/features/leads/api/campaigns";

/**
 * Jedna kampania: firmy, treści i kolejka.
 *
 * Zatwierdzanie jest hurtowe, ale podgląd jest pojedynczy — mail wysłany czwartego
 * dnia był akceptowany pierwszego, więc do momentu wysyłki każdy da się jeszcze
 * wyjąć z kolejki: pojedynczo („wyjmij z kolejki" przy wierszu) albo wszystkie
 * naraz, razem z przypomnieniami, które dopiero by powstały („Zakończ kampanię").
 */

/** Polska liczba mnoga: plural(3, "mail", "maile", "maili") → „maile". */
function plural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const tens = n % 100;
  if (n % 10 >= 2 && n % 10 <= 4 && (tens < 12 || tens > 14)) return few;
  return many;
}

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: campaign } = useCampaign(id);
  const { data: messages, isLoading } = useCampaignMessages(id);
  const addRecipients = useAddRecipients();
  const personalize = usePersonalizeCampaign();
  const { data: spam } = useSpamChecks(id);
  const runSpamCheck = useRunSpamCheck();
  const latestSpam = spam?.checks?.[0];
  const approve = useApproveMessages();
  const cancel = useCancelMessages();
  const resume = useResumeRecipient();
  const runAction = useCampaignAction();
  const finish = useFinishCampaign();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [picking, setPicking] = useState(false);
  const [preview, setPreview] = useState<CampaignMessage | null>(null);
  const [outcome, setOutcome] = useState<AddRecipientsOutcome | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [finished, setFinished] = useState<FinishOutcome | null>(null);
  const { data: queue } = useQueueState();

  usePageTitle(campaign ? `Kampania — ${campaign.name}` : "Kampania");

  const drafts = useMemo(
    () => (messages ?? []).filter((row) => row.status === "draft"),
    [messages],
  );
  // Co przepadnie po „Zakończ kampanię" — liczone z tego, co widać w tabeli, żeby
  // potwierdzenie mówiło konkretnie, ile maili i ile firm, a nie „coś zostanie
  // anulowane".
  const pending = useMemo(
    () =>
      (messages ?? []).filter((row) => row.status === "draft" || row.status === "approved"),
    [messages],
  );

  if (isLoading || !campaign) return <PageLoader />;

  const pendingFollowUps = pending.filter((row) => row.step > 0).length;
  const activeRecipients = new Set(
    (messages ?? [])
      .filter((row) => row.recipient_status === "active")
      .map((row) => row.recipient),
  ).size;

  const confirmFinish = () => {
    const lines = [`Zakończyć kampanię „${campaign.name}”?`, ""];
    if (pending.length > 0) {
      lines.push(
        `Anulujemy ${pending.length} ${plural(pending.length, "mail czekający", "maile czekające", "maili czekających")} na wysyłkę` +
          (pendingFollowUps > 0
            ? ` (w tym ${pendingFollowUps} ${plural(pendingFollowUps, "przypomnienie", "przypomnienia", "przypomnień")} do firm, które nie odpisały)`
            : "") +
          ".",
      );
    } else {
      lines.push("W kolejce nie czeka żaden mail.");
    }
    lines.push(
      activeRecipients > 0
        ? `${activeRecipients} ${plural(activeRecipients, "firma w sekwencji nie dostanie", "firmy w sekwencji nie dostaną", "firm w sekwencji nie dostanie")} już żadnego przypomnienia.`
        : "Żadna firma nie jest już w sekwencji.",
    );
    lines.push("", "Tego nie da się cofnąć — zakończonej kampanii nie uruchomisz ponownie.");
    if (!confirm(lines.join("\n"))) return;
    finish.mutate(campaign.id, { onSuccess: (result) => setFinished(result) });
  };

  // Trzy niezależne bramki w trzech miejscach — bez wypisania ich wprost
  // „Uruchom" wygląda jak przycisk, który nie działa.
  const blockers: string[] = [];
  if (queue && !queue.sending_enabled)
    blockers.push("wysyłka wyłączona globalnie (LEADS_MAIL_ENABLED)");
  if (campaign.status !== "running")
    blockers.push(`kampania: ${campaign.status_label.toLowerCase()}`);
  if (campaign.counts.queued === 0 && drafts.length > 0)
    blockers.push(`${drafts.length} maili czeka na zatwierdzenie`);
  if (campaign.counts.queued === 0 && drafts.length === 0)
    blockers.push("brak maili w kolejce");
  if (queue && !queue.within_window && blockers.length === 0)
    blockers.push(`poza oknem wysyłki (${queue.window})`);

  const toggle = (messageId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/leads/campaigns" className="section-label hover:text-[var(--color-primary)]">
            ← Kampanie
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {campaign.name}
            </h2>
            <Badge color={CAMPAIGN_STATUS_COLORS[campaign.status]}>{campaign.status_label}</Badge>
            {campaign.counts.replies > 0 && (
              <Link to="/leads/inbox" title="Przejdź do skrzynki zakupy@">
                <Badge color="red">
                  {campaign.counts.replies}{" "}
                  {campaign.counts.replies === 1 ? "odpowiedź" : "odpowiedzi"}
                </Badge>
              </Link>
            )}
          </div>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">{campaign.subject_template}</p>
          {(campaign.attachments ?? []).length > 0 && (
            <p
              className="mt-1 flex items-center gap-1 text-xs text-[var(--color-text-muted)]"
              title="Załączniki idą z każdym mailem kampanii"
            >
              <Paperclip size={11} className="shrink-0" />
              <span className="truncate">
                {campaign.attachments.map((file) => file.original_filename).join(", ")}
              </span>
            </p>
          )}
        </div>
        {/* Przyciski potrzebne na co dzień plus „Zakończ kampanię" — nieodwracalne,
            ale to jedyna droga, żeby wyłączyć przypomnienia dla całej kampanii, więc
            ma być widoczne (zgłoszenie z 22.09.2026: schowane pod „…" nikt nie
            znalazł). Rzadkie i płatne siedzi pod „…". Zakończona kampania nie ma już
            czego uruchamiać ani do kogo pisać — zostaje sama data. */}
        <div className="flex flex-wrap items-center gap-2">
          {campaign.status === "done" ? (
            <span className="text-xs text-[var(--color-text-muted)]">
              Zakończona
              {campaign.finished_at
                ? ` ${format(new Date(campaign.finished_at), "dd.MM.yyyy HH:mm")}`
                : ""}{" "}
              — nic z tej kampanii już nie wyjdzie.
            </span>
          ) : (
            <>
              <Button size="sm" variant="secondary" onClick={() => setPicking(true)}>
                <Plus size={13} className="mr-1.5 inline" /> Dodaj firmy
              </Button>
              {campaign.needs_personalization && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={personalize.isPending}
                  onClick={() =>
                    personalize.mutate(
                      { id: campaign.id },
                      {
                        onSuccess: (result) => {
                          if (result?.message) alert(result.message);
                        },
                      },
                    )
                  }
                  title="Pisze akapit AI dla firm, które go jeszcze nie mają. Gotowych nie rusza."
                >
                  <Sparkles size={13} className="mr-1.5 inline" /> Dopisz akapity AI
                </Button>
              )}
              {campaign.status !== "running" ? (
                <Button
                  size="sm"
                  onClick={() => runAction.mutate({ id: campaign.id, action: "start" })}
                  title="Odblokowuje kolejkę tej kampanii. Wychodzą tylko maile zatwierdzone."
                >
                  <Play size={13} className="mr-1.5 inline" /> Uruchom kampanię
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => runAction.mutate({ id: campaign.id, action: "pause" })}
                  title="Zatrzymuje kolejkę. Nic nie wychodzi do ponownego uruchomienia."
                >
                  <Pause size={13} className="mr-1.5 inline" /> Wstrzymaj
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                loading={finish.isPending}
                onClick={confirmFinish}
                title="Kończy kampanię na stałe: wyjmuje z kolejki wszystko, co jeszcze czeka, i wyłącza przypomnienia dla wszystkich jej odbiorców."
              >
                <CircleStop size={13} className="mr-1.5 inline" /> Zakończ kampanię
              </Button>
              {campaign.needs_personalization && (
                <button
                  type="button"
                  onClick={() => setMoreOpen((open) => !open)}
                  className="border border-[var(--color-border)] px-2 py-1.5 text-xs text-[var(--color-text-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-text-primary)]"
                  title="Rzadziej używane"
                >
                  <MoreHorizontal size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {finished && (
        <div className="card mb-4 p-3 text-sm">
          <p>
            Kampania zakończona. Anulowano{" "}
            <span className="font-mono">{finished.cancelled}</span>{" "}
            {plural(finished.cancelled, "mail", "maile", "maili")}
            {finished.follow_ups_cancelled > 0 && (
              <>
                {" "}
                (w tym <span className="font-mono">{finished.follow_ups_cancelled}</span>{" "}
                {plural(finished.follow_ups_cancelled, "przypomnienie", "przypomnienia", "przypomnień")})
              </>
            )}
            ; sekwencję zakończono{" "}
            <span className="font-mono">{finished.recipients_finished}</span>{" "}
            {plural(finished.recipients_finished, "firmie", "firmom", "firmom")}.
          </p>
        </div>
      )}

      {moreOpen && campaign.status !== "done" && campaign.needs_personalization && (
        <div className="card mb-4 space-y-2 p-3 text-xs">
          {campaign.needs_personalization && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[var(--color-text-secondary)]">
                <b className="text-[var(--color-text-primary)]">Napisz akapity od nowa</b> —
                kasuje akapity AI także tam, gdzie były poprawiane ręcznie, i płaci
                za każdą firmę jeszcze raz.
              </span>
              <Button
                size="sm"
                variant="ghost"
                loading={personalize.isPending}
                onClick={() => {
                  if (
                    !confirm(
                      "Napisać akapity od nowa dla WSZYSTKICH firm czekających na " +
                        "zatwierdzenie?\n\nRęczne poprawki w akapitach zostaną nadpisane, " +
                        "a każda firma to kolejne płatne zapytanie do AI.",
                    )
                  )
                    return;
                  personalize.mutate({ id: campaign.id, force: true });
                }}
              >
                Napisz od nowa
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Jedno zdanie zamiast domyślania się z pięciu przycisków, dlaczego nic nie
          wychodzi. Blokady są trzy i leżą w trzech różnych miejscach. */}
      <div className="card mb-4 p-3 text-xs">
        <span className="section-label mr-2">Czy coś wychodzi</span>
        {blockers.length === 0 ? (
          <span className="text-[var(--color-text-primary)]">
            Tak — kolejka pracuje.
          </span>
        ) : (
          <>
            <span className="text-[var(--color-primary)]">Nie. </span>
            <span className="text-[var(--color-text-secondary)]">
              {blockers.join(" · ")}
            </span>
          </>
        )}
      </div>

      {/* Kontrola antyspamowa treści. Odpala się SAMA — przy starcie kampanii i raz
          w tygodniu — bo kontrola wymagająca pamiętania robi się raz i nigdy więcej.
          Tu jest tylko wynik i przycisk „sprawdź teraz" dla świeżo poprawionej treści. */}
      <div className="card mb-4 p-3 text-xs">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="section-label">Czy wyglądamy jak spam</span>
          <Button
            size="sm"
            variant="ghost"
            loading={runSpamCheck.isPending}
            disabled={!spam?.enabled}
            onClick={() => runSpamCheck.mutate(campaign.id)}
          >
            Sprawdź teraz
          </Button>
        </div>

        {!spam?.enabled ? (
          <p className="mt-1 text-[var(--color-text-secondary)]">
            Kontrola chwilowo niedostępna.
          </p>
        ) : !latestSpam ? (
          <p className="mt-1 text-[var(--color-text-secondary)]">
            Jeszcze nie badaliśmy tej treści. Kontrola ruszy sama przy starcie
            kampanii i powtórzy się co poniedziałek.
          </p>
        ) : latestSpam.status === "pending" ? (
          <p className="mt-1 text-[var(--color-text-secondary)]">
            Badanie w toku — wynik zwykle po kilku minutach.
          </p>
        ) : latestSpam.status === "failed" ? (
          <p className="mt-1 text-[var(--color-primary)]">
            Nie udało się pobrać wyniku: {latestSpam.error}
          </p>
        ) : (
          <>
            <div className="mt-1 flex flex-wrap items-baseline gap-2">
              <span
                className={
                  latestSpam.needs_attention
                    ? "font-mono text-2xl text-[var(--color-primary)]"
                    : "font-mono text-2xl text-[var(--color-text-primary)]"
                }
              >
                {latestSpam.score}/10
              </span>
              <span className="text-[var(--color-text-secondary)]">
                {latestSpam.summary}
              </span>
              {latestSpam.checked_at && (
                <span className="text-[var(--color-text-muted)]">
                  ({format(new Date(latestSpam.checked_at), "dd.MM.yyyy HH:mm")}, treść
                  dla firmy „{latestSpam.sample_company}")
                </span>
              )}
            </div>
            {/* Dwie skale naraz mylą: nasza „10 = dobrze" vs SpamAssassin „5 = spam".
                Jedno zdanie wyjaśnienia oszczędza pytanie, które inaczej zada każdy. */}
            <p className="mt-1 text-[var(--color-text-muted)]">
              Skala 0–10: im więcej, tym lepiej. SpamAssassin liczy odwrotnie —
              punkty dodatnie to sygnały spamu (od 5 pkt mail uchodzi za spam),
              wynik ujemny znaczy, że przeważają sygnały wiarygodności. Nasza
              ocena to 10 minus jego punkty.
            </p>
            {Object.keys(latestSpam.auth).length > 0 && (
              <p className="mt-1 text-[var(--color-text-secondary)]">
                Uwierzytelnienie:{" "}
                {Object.entries(latestSpam.auth)
                  .map(([key, ok]) => `${key.toUpperCase()} ${ok ? "✓" : "✗"}`)
                  .join(" · ")}
              </p>
            )}
            {latestSpam.issues.length > 0 && (
              <ul className="mt-1.5 space-y-0.5 text-[var(--color-text-secondary)]">
                {latestSpam.issues.map((issue) => (
                  <li key={issue.code}>
                    <span className="font-mono text-[var(--color-primary)]">
                      −{issue.score}
                    </span>{" "}
                    {issue.detail || issue.code}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      {outcome && (
        <div className="card mb-4 p-3 text-sm">
          <p>
            Dodano firm: <span className="font-mono">{outcome.added.length}</span>
          </p>
          {outcome.skipped.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-xs text-[var(--color-text-secondary)]">
              {outcome.skipped.map((row) => (
                <li key={row.lead}>
                  <span className="text-[var(--color-primary)]">pominięto</span> {row.name} —{" "}
                  {row.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="section-label">
          {drafts.length > 0
            ? `${drafts.length} maili czeka na zatwierdzenie`
            : "Wszystko zatwierdzone"}
        </span>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="secondary"
          disabled={selected.size === 0}
          loading={cancel.isPending}
          onClick={() =>
            cancel.mutate(
              { id: campaign.id, messageIds: [...selected] },
              { onSuccess: () => setSelected(new Set()) },
            )
          }
        >
          <X size={13} className="mr-1.5 inline" /> Anuluj ({selected.size})
        </Button>
        <Button
          size="sm"
          disabled={selected.size === 0}
          loading={approve.isPending}
          onClick={() =>
            approve.mutate(
              { id: campaign.id, messageIds: [...selected] },
              { onSuccess: () => setSelected(new Set()) },
            )
          }
        >
          <Check size={13} className="mr-1.5 inline" /> Zatwierdź do wysyłki ({selected.size})
        </Button>
      </div>

      {(messages ?? []).length === 0 ? (
        <div className="card">
          <EmptyState>Kampania nie ma jeszcze żadnej firmy.</EmptyState>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th className="w-10">
                <input
                  type="checkbox"
                  checked={drafts.length > 0 && selected.size === drafts.length}
                  onChange={(e) =>
                    setSelected(e.target.checked ? new Set(drafts.map((m) => m.id)) : new Set())
                  }
                  title="Zaznacz wszystko, co czeka na zatwierdzenie"
                />
              </Th>
              <Th>Firma</Th>
              <Th>Krok</Th>
              <Th>Akapit AI</Th>
              <Th>Status</Th>
              <Th>Termin / wysłano</Th>
            </tr>
          </thead>
          <tbody>
            {(messages ?? []).map((row) => (
              <tr key={row.id} className="border-t border-[var(--color-border)] align-top">
                <td className="px-3 py-2.5">
                  {row.status === "draft" && (
                    <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggle(row.id)} />
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => setPreview(row)}
                    className="text-left text-sm text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                  >
                    {row.lead_name}
                  </button>
                  <div className="font-mono text-[11px] text-[var(--color-text-muted)]">
                    {row.email}
                  </div>
                  {row.recipient_status === "stopped" && (
                    <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-[var(--color-primary)]">
                      <Ban size={10} /> {row.stop_reason}
                      {/* Autoresponder („jestem na urlopie") wygląda dla dyspozytora
                          jak odpowiedź firmy. Odróżnia je człowiek — stąd cofnięcie. */}
                      <button
                        type="button"
                        disabled={resume.isPending}
                        onClick={() =>
                          resume.mutate(
                            { id: campaign.id, recipient: row.recipient },
                            {
                              onError: (err) =>
                                alert(
                                  (err as { response?: { data?: { errors?: { recipient?: string[] } } } })
                                    ?.response?.data?.errors?.recipient?.[0] ??
                                    "Nie udało się wznowić sekwencji.",
                                ),
                            },
                          )
                        }
                        className="underline decoration-dotted underline-offset-2 hover:no-underline"
                        title="Cofa zatrzymanie — mail wraca do zatwierdzenia, nie wychodzi sam"
                      >
                        wznów
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px]">
                  {row.step === 0 ? "zapytanie" : `przypomnienie ${row.step}`}
                </td>
                <td className="px-3 py-2.5 text-[11px] text-[var(--color-text-secondary)]">
                  {row.personalization ? (
                    <span title={row.personalization}>
                      {row.personalization.length > 90
                        ? `${row.personalization.slice(0, 90)}…`
                        : row.personalization}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-muted)]">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <Badge color={MESSAGE_STATUS_COLORS[row.status]}>{row.status_label}</Badge>
                  {row.status === "approved" && campaign.status !== "done" && (
                    /* Zatwierdzony mail nie ma checkboxa (zaznaczanie jest do
                       zatwierdzania szkiców), a do wysyłki mogą minąć dni — stąd
                       osobna droga wyjęcia go z kolejki. */
                    <button
                      type="button"
                      disabled={cancel.isPending}
                      onClick={() => {
                        if (
                          !confirm(
                            `Wyjąć z kolejki ${row.step === 0 ? "zapytanie" : `przypomnienie ${row.step}`} do „${row.lead_name}”?\n\n` +
                              "Ten mail nie wyjdzie ani teraz, ani później. Kolejnych przypomnień " +
                              "dla tej firmy też nie będzie — powstają dopiero po wysłanym mailu.",
                          )
                        )
                          return;
                        cancel.mutate({ id: campaign.id, messageIds: [row.id] });
                      }}
                      className="mt-1 block text-[11px] text-[var(--color-text-muted)] underline decoration-dotted underline-offset-2 hover:text-[var(--color-primary)] hover:no-underline"
                      title="Anuluje ten mail; wysłane wcześniej zostają w historii"
                    >
                      wyjmij z kolejki
                    </button>
                  )}
                  {row.error && (
                    <div className="mt-1 flex items-start gap-1 text-[11px] text-[var(--color-primary)]">
                      <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                      {row.error}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-[var(--color-text-secondary)]">
                  {row.sent_at
                    ? format(new Date(row.sent_at), "dd.MM.yyyy HH:mm")
                    : row.scheduled_at
                      ? format(new Date(row.scheduled_at), "dd.MM.yyyy HH:mm")
                      : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}

      <RecipientPicker
        open={picking}
        campaignId={campaign.id}
        onClose={() => setPicking(false)}
        onPick={(leadIds) =>
          addRecipients.mutate(
            { id: campaign.id, leadIds },
            {
              onSuccess: (data) => {
                setOutcome(data as AddRecipientsOutcome);
                setPicking(false);
              },
            },
          )
        }
        pending={addRecipients.isPending}
      />

      <MessageEditor
        messageId={preview?.id}
        leadName={preview?.lead_name ?? ""}
        bodyFormat={campaign.body_format ?? "markdown"}
        onClose={() => setPreview(null)}
      />
    </>
  );
}

/**
 * Podgląd i poprawka jednej wiadomości.
 *
 * Dwie zakładki, bo to dwie różne rzeczy: „Jak zobaczy adresat" to HTML składany
 * DOKŁADNIE tą funkcją, która buduje maila przy wysyłce, a „Treść" to źródło,
 * które można poprawić ręcznie. Podgląd renderujący inaczej niż nadawca byłby
 * gorszy niż jego brak.
 */
function MessageEditor({
  messageId,
  leadName,
  bodyFormat,
  onClose,
}: {
  messageId: string | undefined;
  leadName: string;
  /** Format kampanii — źródło wiadomości poprawia się tym samym edytorem,
   *  którym napisano szablon (HTML → edytor wizualny, tekst → textarea). */
  bodyFormat: CampaignBodyFormat;
  onClose: () => void;
}) {
  const { data: message } = useCampaignMessage(messageId);
  const save = useSaveMessage();
  const recompose = useRecomposeMessage();

  const [tab, setTab] = useState<"html" | "source">("html");
  const [draft, setDraft] = useState<{ subject: string; body: string } | null>(null);

  // Reset przy każdej nowej wiadomości — inaczej w oknie zostaje poprzednia treść.
  useEffect(() => {
    setDraft(null);
    setTab("html");
  }, [messageId]);

  const subject = draft?.subject ?? message?.subject ?? "";
  const body = draft?.body ?? message?.body ?? "";
  const dirty =
    !!message && (subject !== message.subject || body !== message.body);
  const editable = message?.can_edit ?? false;

  return (
    <Modal
      open={!!messageId}
      onClose={onClose}
      title={leadName ? `Wiadomość — ${leadName}` : "Wiadomość"}
      size="lg"
    >
      {!message ? (
        <p className="text-sm text-[var(--color-text-muted)]">Wczytywanie…</p>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
            <span>
              <span className="section-label mr-1.5">Do</span>
              <span className="font-mono">{message.email}</span>
            </span>
            {!editable && (
              <span className="text-[var(--color-text-muted)]">
                {message.status_label} — treści już nie można zmienić
              </span>
            )}
          </div>

          <div>
            <label className="section-label mb-1 block">Temat</label>
            <input
              className="input-base"
              value={subject}
              disabled={!editable}
              onChange={(e) => setDraft({ subject: e.target.value, body })}
            />
          </div>

          <div className="flex items-center gap-1 border-b border-[var(--color-border)]">
            <button
              type="button"
              onClick={() => setTab("html")}
              className={`px-3 py-1.5 text-xs ${
                tab === "html"
                  ? "border-b-2 border-[var(--color-primary)] text-[var(--color-text-primary)]"
                  : "text-[var(--color-text-muted)]"
              }`}
            >
              Jak zobaczy adresat
            </button>
            <button
              type="button"
              onClick={() => setTab("source")}
              className={`px-3 py-1.5 text-xs ${
                tab === "source"
                  ? "border-b-2 border-[var(--color-primary)] text-[var(--color-text-primary)]"
                  : "text-[var(--color-text-muted)]"
              }`}
            >
              Treść do poprawki
            </button>
          </div>

          {tab === "html" ? (
            <div
              className="rich-text-content max-h-[26rem] overflow-y-auto border border-[var(--color-border)] bg-white p-4 text-[13px] text-black"
              // Źródłem jest nasz własny szablon plus akapit AI, składane po stronie
              // serwera tą samą funkcją co mail — nie treść od obcego nadawcy.
              dangerouslySetInnerHTML={{
                __html: dirty
                  ? "<p><em>Zapisz zmiany, żeby zobaczyć nową wersję.</em></p>"
                  : message.body_html,
              }}
            />
          ) : bodyFormat === "html" ? (
            <RichTextEditor
              value={body}
              disabled={!editable}
              onChange={(html) => setDraft({ subject, body: html })}
              placeholder="Treść maila…"
            />
          ) : (
            <textarea
              className="input-base min-h-[20rem] font-mono text-xs"
              value={body}
              disabled={!editable}
              onChange={(e) => setDraft({ subject, body: e.target.value })}
            />
          )}

          {editable && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                loading={recompose.isPending}
                onClick={() => {
                  if (
                    !confirm(
                      "Złożyć treść od nowa z aktualnego szablonu kampanii?\n\n" +
                        "Ręczne poprawki w tej wiadomości zostaną nadpisane. " +
                        "Akapit AI zostaje.",
                    )
                  )
                    return;
                  recompose.mutate(message.id, {
                    onSuccess: () => setDraft(null),
                  });
                }}
                title="Przydatne, gdy szablon zmienił się po przygotowaniu tej wiadomości"
              >
                Złóż od nowa z szablonu
              </Button>
              <div className="flex items-center gap-2">
                {dirty && (
                  <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
                    Cofnij zmiany
                  </Button>
                )}
                <Button
                  size="sm"
                  disabled={!dirty}
                  loading={save.isPending}
                  onClick={() =>
                    save.mutate(
                      { id: message.id, subject, body },
                      { onSuccess: () => setDraft(null) },
                    )
                  }
                >
                  Zapisz
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Dobieranie firm do kampanii.
 *
 * Domyślnie pokazujemy tylko firmy, których nie ma w żadnej innej kampanii:
 * druga zimna wiadomość z tego samego działu to dokładnie ten mail, po którym
 * ludzie się wypisują. Odbiorcy TEJ kampanii nie liczą się jako „inna" —
 * inaczej dobieranie w dwóch turach niczego by nie pokazało.
 */
export function RecipientPicker({
  open,
  campaignId,
  onClose,
  onPick,
  pending,
}: {
  open: boolean;
  campaignId: string;
  onClose: () => void;
  onPick: (leadIds: string[]) => void;
  pending: boolean;
}) {
  const [search, setSearch] = useState("");
  const [fromSearch, setFromSearch] = useState("");
  const [onlyFresh, setOnlyFresh] = useState(true);
  const { data: searches } = useLeadSearches();
  const { data: leads } = useLeads({
    q: search,
    search: fromSearch || undefined,
    not_in_other_campaigns: onlyFresh,
    campaign: campaignId,
  });
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  const rows = (leads ?? []).filter((lead) => lead.primary_contact?.email);
  const sourceSearches = (searches ?? []).filter((row) => row.counts.accepted > 0);

  // Każde otwarcie zaczyna od pustego zaznaczenia. Bez tego okno pamięta firmy
  // z poprzedniego dobierania — po dodaniu zostałyby odhaczone i drugie
  // „Dodaj" wysyłałoby je jeszcze raz (backend odbije je jako „już w kampanii",
  // ale użytkownik widziałby ptaszki przy firmach, których nie zaznaczył).
  useEffect(() => {
    if (open) setChosen(new Set());
  }, [open]);

  // „Zaznacz wszystkie" pracuje na tym, co widać po filtrach, ale zaznaczenia
  // spoza widoku nie kasuje: dobieranie w dwóch turach (raz z jednego
  // wyszukiwania, raz z drugiego) to normalny sposób pracy.
  const chosenVisible = rows.filter((lead) => chosen.has(lead.id)).length;
  const allVisibleChosen = rows.length > 0 && chosenVisible === rows.length;
  const chosenHidden = chosen.size - chosenVisible;
  const toggleAllVisible = (checked: boolean) =>
    setChosen((prev) => {
      const next = new Set(prev);
      for (const lead of rows) {
        if (checked) next.add(lead.id);
        else next.delete(lead.id);
      }
      return next;
    });

  return (
    <Modal open={open} onClose={onClose} title="Dodaj firmy do kampanii" size="lg">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          className="input-base min-w-[220px] flex-1"
          placeholder="Szukaj po nazwie, NIP, mieście, wyszukiwaniu…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="input-base sm:w-auto sm:min-w-[220px]"
          value={fromSearch}
          onChange={(e) => setFromSearch(e.target.value)}
        >
          <option value="">Z dowolnego wyszukiwania</option>
          {sourceSearches.map((row) => (
            <option key={row.id} value={row.id}>
              {row.prompt.length > 60 ? `${row.prompt.slice(0, 60)}…` : row.prompt} (
              {row.counts.accepted})
            </option>
          ))}
        </select>
      </div>
      <label className="mb-2 flex cursor-pointer items-center gap-2 text-xs text-[var(--color-text-secondary)]">
        <input
          type="checkbox"
          checked={onlyFresh}
          onChange={(e) => setOnlyFresh(e.target.checked)}
        />
        Tylko firmy, których nie ma w żadnej innej kampanii
      </label>
      <p className="section-label mb-2">
        Pokazujemy tylko firmy z adresem e-mail — bez adresu nie ma czego wysłać.
      </p>
      <div className="max-h-80 overflow-y-auto border border-[var(--color-border)]">
        {rows.length === 0 ? (
          <p className="p-4 text-sm text-[var(--color-text-muted)]">
            {onlyFresh
              ? "Brak firm z adresem e-mail spoza innych kampanii — odznacz filtr, żeby zobaczyć pozostałe."
              : "Brak firm z adresem e-mail."}
          </p>
        ) : (
          <>
            <label className="sticky top-0 z-10 flex cursor-pointer items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs font-semibold text-[var(--color-text-secondary)]">
              <input
                type="checkbox"
                checked={allVisibleChosen}
                ref={(el) => {
                  if (el) el.indeterminate = chosenVisible > 0 && !allVisibleChosen;
                }}
                onChange={(e) => toggleAllVisible(e.target.checked)}
              />
              Zaznacz wszystkie ({rows.length})
              {chosenVisible > 0 && !allVisibleChosen && (
                <span className="font-normal text-[var(--color-text-muted)]">
                  — zaznaczono {chosenVisible}
                </span>
              )}
            </label>
            {rows.map((lead) => (
              <label
                key={lead.id}
                className="flex cursor-pointer items-center gap-2 border-b border-[var(--color-border)] px-3 py-2 text-sm last:border-0 hover:bg-[var(--color-bg-elevated)]"
              >
                <input
                  type="checkbox"
                  checked={chosen.has(lead.id)}
                  onChange={() =>
                    setChosen((prev) => {
                      const next = new Set(prev);
                      if (next.has(lead.id)) next.delete(lead.id);
                      else next.add(lead.id);
                      return next;
                    })
                  }
                />
                <span className="flex-1">
                  {lead.name}
                  <span className="ml-2 font-mono text-[11px] text-[var(--color-text-muted)]">
                    {lead.primary_contact?.email}
                  </span>
                  {lead.source_search && (
                    <span
                      className="block text-[11px] text-[var(--color-text-muted)]"
                      title={lead.source_search.prompt}
                    >
                      z wyszukiwania:{" "}
                      {lead.source_search.prompt.length > 70
                        ? `${lead.source_search.prompt.slice(0, 70)}…`
                        : lead.source_search.prompt}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-[var(--color-text-muted)]">{lead.status_label}</span>
              </label>
            ))}
          </>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {chosenHidden > 0 && (
          <span className="mr-auto text-[11px] text-[var(--color-text-muted)]">
            W tym {chosenHidden} spoza widocznej listy — zaznaczone przy innym filtrze.
          </span>
        )}
        <Button variant="ghost" onClick={onClose}>
          Anuluj
        </Button>
        <Button disabled={chosen.size === 0} loading={pending} onClick={() => onPick([...chosen])}>
          Dodaj ({chosen.size})
        </Button>
      </div>
    </Modal>
  );
}
